import { createEffect, createResource, For, JSX, Show, Suspense } from "solid-js";
import { requireVisitorCheckinContext } from "../VisitorCheckinContext";
import { createProxySignal, IPrinter, toast, visitorManagementClient } from "common";
import { visitorCheckin as api } from 'st-app/api'
import styles from './print-step.module.css'
import Flexbox from "../../st-flexbox/st-flexbox";
import Select from "../../st-select/st-select";
import Loader from "../../st-loader/st-loader";
import Button from "../../st-button/st-button";
import GroupBox from "../../st-group-box/st-group-box";
import STInput from "../../st-input/st-input";
import StaffSearchInput from "./staff-search-input";
import Textarea from "../../st-textarea/st-textarea";
import { formatDob, formatName } from "./util";

const VisitorTypeLookup = {
    '0': 'Parent',
    '1': 'Visitor',
    '2': 'Vendor',
}

const REASON_OTHER = '6';

export const PrintStep = () => {
    const checkin = requireVisitorCheckinContext();

    const [printers, { refetch: refreshPrinters }] = createResource<IPrinter[]>(async () => {
        try {
            const available = await visitorManagementClient.listPrinters();
            if (available.length && !checkin.selectedPrinterName()) {
                checkin.setSelectedPrinterName(available[0].name)
            }

            return available;
        } catch (e) {
            console.error(e);
            toast.notify('error', 'An error ocurred while loading the list of available printers')
        }

        return []
    })

    const [reasonLookup] = createResource(async () => {
        return api.GetReasonLookup();
    })

    createEffect(() => {
        const t = checkin.visitorType();
        if (t) {
            checkin.setPrintData('VisitorType', VisitorTypeLookup[t as keyof typeof VisitorTypeLookup] ?? t);
        }
    })

    createEffect(() => {
        const r = checkin.reason();
        const lookup = reasonLookup();
        if (r) {
            checkin.setPrintData('Reason', lookup ? lookup[r] : r);
        }
    })

    const visitorTypeProxy = createProxySignal(() => checkin.visitorType() as string, (t) => checkin.setVisitorType(t as keyof typeof VisitorTypeLookup));

    return (
        <div class={styles['print-step']}>
            <div class={styles['print-step-visitor']}>
                <div class={styles['print-step-visitor-avatar']}>
                    <img src={`data:image/bmp;base64,${checkin.scan()?.photoBase64}`}></img>
                </div>

                <div class={styles['visitor-info']}>
                    <Field label="Full Name:" value={formatName(checkin.scan()?.AAMVA)} />
                    <Field label="Date of Birth:" value={formatDob(checkin.scan()?.AAMVA?.dateOfBirth)} />
                    <Field label="License Number:" value={checkin.scan()?.AAMVA?.idNumber} />
                    <Field label="Issuing State:" value={checkin.scan()?.AAMVA?.issuerIdentificationNumber} />
                </div>
            </div>
            <div style='width: 100%; display: flex; flex-direction: column; gap: 8px;'>
                <Flexbox dir='horizontal' style='align-items: end;'>
                    <div style='max-width: 300px; display: grid; grid-template-columns: 1fr auto; gap: 4px;'>
                        <span>
                            Printer
                        </span>
                        <span></span>
                        <Suspense fallback={<Loader />}>
                            <Show when={!printers.loading}>
                                <Select value={[checkin.selectedPrinterName, checkin.setSelectedPrinterName]}>
                                        <For each={printers()}>{(printer) => (
                                            <Select.Option value={printer.name}>
                                                <span>{printer.name}</span>
                                            </Select.Option>
                                        )}</For>
                                </Select>
                            </Show>
                        </Suspense>
                        <Button style={'align-self: bottom;'} onClick={refreshPrinters} icon='fas fa-arrows-rotate' title='Refresh Printer List' />
                    </div>
                </Flexbox>

                <GroupBox label='Additional Info'>
                    <div style='display: grid; grid-template-columns: 250px 250px; gap: 8px;'>
                        <STInput label='Cell Phone' value={[checkin.cellPhone, checkin.setCellPhone]} />
                        <STInput label='Email' value={[checkin.email, checkin.setEmail]} />
                    </div>
                </GroupBox>
                
                <GroupBox label='Visit Reason & Notes'>
                    <div style='display: grid; grid-template-columns: repeat(3, 180px) 220px; gap: 8px'>
                        <label class={styles['reason-label']}>
                            Reason
                            <Suspense fallback={<Loader />}>
                                <Select required value={[checkin.reason, checkin.setReason]}>
                                        <For each={Object.entries(reasonLookup() ?? {})}>{([key, value]) => (
                                            <Select.Option value={key}>
                                                <span>{value}</span>
                                            </Select.Option>
                                        )}</For>
                                </Select>
                            </Suspense>
                        </label>
                        <label class={styles['reason-label']}>
                            Visitor Type
                            <Select required value={visitorTypeProxy}>
                                <For each={Object.entries(VisitorTypeLookup)}>{([key, value]) => (
                                    <Select.Option value={key}>
                                        <span>{value}</span>
                                    </Select.Option>
                                )}</For>
                            </Select>
                        </label>
                        <label>
                            Meeting With
                            <StaffSearchInput value={[checkin.meetingWith, checkin.setMeetingWith]} displayName={[checkin.meetingWithName, checkin.setMeetingWithName]} />
                        </label>
                    </div>
                    <div style='padding-top: 8px;'>
                        <Textarea label='Notes' value={[checkin.notes, checkin.setNotes]} rows={4} required={checkin.reason() === REASON_OTHER} />
                    </div>
                </GroupBox>
            </div>

            <div class={styles['history-panel']}>
                <span class={styles['history-panel-title']}>Check In History</span>
                <For each={checkin.history()}>{(entry) => (
                    <div class={styles['history-entry']} onClick={() => checkin.loadFromHistoryEntry(entry)}>
                        <div class={styles['history-entry-line']}>
                            <strong>Reason:</strong>
                            <span>{reasonLookup()?.[entry.reason] || entry.reason}</span>
                        </div>
                        <div class={styles['history-entry-line']}>
                            <strong>Type:</strong>
                            <span>{VisitorTypeLookup[entry.visitorType as keyof typeof VisitorTypeLookup] || entry.visitorType}</span>
                        </div>
                        <div class={styles['history-entry-line']}>
                            <strong>Notes:</strong>
                            <span>{entry.notes}</span>
                        </div>
                    </div>
                )}</For>
            </div>
        </div>
    )
}

const Field = (props: { label: string; value: string | JSX.Element | undefined }) => {
    return (
        <div class={styles['field']}>
            <span class={styles['label']}>{props.label}</span>
            <span class={styles['value']}>{props.value ?? 'N/A'}</span>
        </div>
    )
}

